import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Printer, Check, Ban, FileDown } from "lucide-react";

const EMPTY = { item_number: "", qty: "", from_warehouse: "", from_location: "", to_warehouse: "", to_location: "", stock_status: "available", notes: "" };

const ST_LABELS = {
  en_transito: "En tránsito",
  recibida: "Recibida",
  cancelada: "Cancelada",
};
const ST_MAP = {
  en_transito: "pending",
  recibida: "approved",
  cancelada: "rejected",
};

export default function Transfers() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reasonAction, setReasonAction] = useState(null);
  const [reasonText, setReasonText] = useState("");

  const canEdit = can("admin", "warehouse");

  const load = useCallback(() => api.get("/transfers").then((r) => setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/items").then((r) => setItems(r.data));
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
  }, [load]);

  const post = async () => {
    try {
      await api.post("/transfers", { ...form, qty: Number(form.qty) });
      toast.success(t("tr.posted"));
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const receive = async (tr) => {
    if (!window.confirm(`Confirmar recepción de ${tr.qty} × ${tr.item_number} en ${tr.to_warehouse}?`)) return;
    try {
      await api.post(`/transfers/${tr.id}/receive`);
      toast.success(`${tr.number} recibida — stock acreditado`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cancel = async () => {
    try {
      await api.post(`/transfers/${reasonAction.id}/cancel`, { reason: reasonText });
      toast.success(`${reasonAction.number} cancelada — stock devuelto al origen`);
      setReasonAction(null); setReasonText("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const printPdf = (tr) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/transfers/${tr.id}/pdf`;
    const w = window.open(url, "_blank");
    if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) {} });
  };
  const downloadPdf = (tr) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/transfers/${tr.id}/pdf`, "_blank");

  const locOptions = (wh) => locations.filter((l) => l.warehouse === wh);

  const WhLoc = ({ whKey, locKey, whLabel, locLabel }) => (
    <>
      <div className="space-y-1">
        <Label className="text-[10px] font-bold uppercase tracking-widest">{whLabel}</Label>
        <Select value={form[whKey]} onValueChange={(v) => setForm({ ...form, [whKey]: v, [locKey]: "" })}>
          <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`transfer-${whKey}`}><SelectValue /></SelectTrigger>
          <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] font-bold uppercase tracking-widest">{locLabel}</Label>
        <Select value={form[locKey] || "none"} onValueChange={(v) => setForm({ ...form, [locKey]: v === "none" ? "" : v })}>
          <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`transfer-${locKey}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t("loc.none")}</SelectItem>
            {locOptions(form[whKey]).map((l) => <SelectItem key={l.id} value={l.code}>{l.code}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  const filtered = statusFilter === "all"
    ? rows
    : rows.filter((r) => (r.status || "recibida") === statusFilter);

  return (
    <div data-testid="transfers-page">
      <PageHeader title={t("tr.title")} subtitle="Flujo de 2 pasos — el almacén receptor debe confirmar antes de acreditar el stock" />

      {canEdit && (
        <div className="border bg-card p-4 mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("tr.new")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="space-y-1 col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.item")}</Label>
              <Select value={form.item_number} onValueChange={(v) => setForm({ ...form, item_number: v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="transfer-item-select"><SelectValue /></SelectTrigger>
                <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.item_number}>{i.item_number} — {i.description}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.qty")}</Label>
              <Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })}
                className="rounded-none h-8 text-xs" data-testid="transfer-qty-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.status")}</Label>
              <Select value={form.stock_status} onValueChange={(v) => setForm({ ...form, stock_status: v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="transfer-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["available", "quarantine", "hold", "damaged"].map((s) => <SelectItem key={s} value={s}>{t(`st.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <WhLoc whKey="from_warehouse" locKey="from_location" whLabel={t("tr.fromWh")} locLabel={t("tr.fromLoc")} />
            <WhLoc whKey="to_warehouse" locKey="to_location" whLabel={t("tr.toWh")} locLabel={t("tr.toLoc")} />
          </div>
          <Button onClick={post} disabled={!form.item_number || !form.qty || !form.from_warehouse || !form.to_warehouse}
            className="rounded-none font-bold text-xs" data-testid="transfer-post-button">Enviar (en tránsito)</Button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none h-9 w-48 text-xs" data-testid="transfer-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estatus</SelectItem>
            <SelectItem value="en_transito">En tránsito</SelectItem>
            <SelectItem value="recibida">Recibida</SelectItem>
            <SelectItem value="cancelada">Cancelada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="transfers-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("common.number")}</th><th>{t("common.item")}</th><th className="num">{t("common.qty")}</th>
              <th>{t("tr.from")}</th><th>{t("tr.to")}</th><th>Estatus</th>
              <th>{t("common.user")}</th><th>{t("common.date")}</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => {
                const status = r.status || "recibida"; // legacy transfers auto-completed
                return (
                  <tr key={r.id} data-testid={`transfer-row-${r.id}`}>
                    <td className="font-bold">{r.number}</td>
                    <td>{r.item_number}</td>
                    <td className="num font-bold">{fmtQty(r.qty)}</td>
                    <td>{r.from_warehouse}{r.from_location ? ` / ${r.from_location}` : ""}</td>
                    <td>{r.to_warehouse}{r.to_location ? ` / ${r.to_location}` : ""}</td>
                    <td><StatusBadge status={ST_MAP[status] || "approved"} /> <span className="text-[10px] font-bold uppercase">{ST_LABELS[status] || "Recibida"}</span></td>
                    <td className="text-muted-foreground">{r.created_by}</td>
                    <td className="text-muted-foreground">{fmtDate(r.created_at)}</td>
                    <td>
                      <div className="flex gap-1">
                        {status === "en_transito" && canEdit && (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-primary"
                                    onClick={() => receive(r)} data-testid={`transfer-receive-${r.id}`}
                                    title="Confirmar recepción">
                              <Check size={12} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-destructive"
                                    onClick={() => { setReasonAction({ id: r.id, number: r.number }); setReasonText(""); }}
                                    data-testid={`transfer-cancel-${r.id}`} title="Cancelar y devolver al origen">
                              <Ban size={12} />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                                onClick={() => printPdf(r)} data-testid={`transfer-print-${r.id}`}
                                title="Imprimir carátula">
                          <Printer size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                                onClick={() => downloadPdf(r)} data-testid={`transfer-pdf-${r.id}`}
                                title="Descargar PDF">
                          <FileDown size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reasonAction && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setReasonAction(null)}>
          <div className="bg-card border p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-bold mb-2">Cancelar transferencia {reasonAction.number}</div>
            <div className="text-xs text-muted-foreground mb-3">El stock será devuelto al almacén origen. Ingresa el motivo:</div>
            <Input value={reasonText} onChange={(e) => setReasonText(e.target.value)}
                   placeholder="Motivo de cancelación" className="rounded-none h-9 text-xs mb-3"
                   data-testid="transfer-cancel-reason" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                      onClick={() => setReasonAction(null)}>Cerrar</Button>
              <Button size="sm" variant="destructive" className="rounded-none text-xs font-bold"
                      onClick={cancel} data-testid="transfer-cancel-confirm">Confirmar cancelación</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
